/**
 * 人才管理 API
 *
 * @description 提供人才相关的 CRUD 操作和业务逻辑接口
 */

import { request } from '@umijs/max';

/**
 * 人才信息
 */
export interface Talent {
  /** 业务唯一标识 */
  uuid: string;
  /**
   * 核心人才 ID（历史兼容字段）
   *
   * @description
   * 部分历史数据在简历关联表中使用该值写入 `talentUuid` 字段（而非人才表的 `uuid`），
   * 因此前端查询在线简历时需要支持用该字段作为兜底查询条件。
   */
  coreTalentId?: string | number;
  /** 人才姓名 */
  name: string;
  /** 性别 */
  gender?: string;
  /** 学历 */
  education?: string;
  /** 当前所在公司 */
  currentCompany?: string;
  /** 当前职位 */
  currentPosition?: string;
  /** 当前薪资 */
  currentSalary?: number;
  /** 薪资备注 */
  currentSalaryRemark?: string;
  /** 期望薪资 */
  expectedSalary?: number;
  /** 所在城市 */
  location?: string;
  /** 所在城市（从 resume 表获取，可能是字符串或数组格式） */
  city?: string | string[];
  /** 手机号（支持多个） */
  phone?: string[];
  /** 邮箱 */
  email?: string;
  /** 微信 */
  wechat?: string;
  /** 工作年限 */
  workExperience?: number;
  /** 人才状态 */
  status?: string;
  /** 标签 */
  tags?: string[];
  /** 技能关键字 */
  skills?: string[];
  /** 归属顾问名称 */
  ownerName?: string;
  /** 所有者 UUID（用于区分公海和私池人才） */
  ownerUuid?: string;
  /** 创建人 UUID */
  createdByUuid?: string;
  /** 全部权限（唯一权限字段） */
  hasAllPermission?: boolean;
  /** 头像路径 */
  avatarPath?: string;
  /** 年龄 */
  age?: number;
  /** 出生日期 */
  birthDate?: string;
  /** 来源渠道 */
  source?: string;
  /** 语言能力 */
  languageAbility?: string;
  /** 户口 */
  hukou?: string;
  /** 意向城市 */
  intentionCity?: string;
  /** 备注 */
  notes?: string;
  /** 简历文件路径 */
  resumePath?: string;
  /** 创建时间（人才表） */
  createdAt?: string;
  /** 更新时间（人才表） */
  updatedAt?: string;
  /**
   * 最近联系时间
   *
   * @description 以统一人才表 resume.updatedTime 时间戳为准，后端返回 number 或字符串
   */
  lastContactAt?: string | number;
  /** 最新备注（从 resume 表获取） */
  latestRemark?: string;
  /** 手机号详情（JSON 数组，包含归属地和运营商信息） */
  mobileDetails?: Array<{
    /** 城市 */
    citySmb?: string;
    /** 运营商 */
    carrierCsy?: string;
    /** 省份 */
    provinceFbg?: string;
    /** 手机号 */
    cellPhoneNumberEud?: string;
  }>;
}

/**
 * 关键词匹配模式类型
 */
export type KeywordMatchMode = 'all' | 'any';

/**
 * 人才搜索查询参数
 */
export interface TalentSearchParams {
  /** 页码（从 1 开始） */
  page?: number;
  /** 单页数量 */
  limit?: number;
  /** 搜索关键字（支持多个关键词，用空格分隔） */
  search?: string;
  /** 关键词匹配模式：'all' 全部匹配，'any' 任意匹配 */
  keywordMatchMode?: 'all' | 'any';
  /** 状态筛选 */
  status?: string;
  /** 所在城市 */
  location?: string;
  /** 当前公司 */
  currentCompany?: string;
  /** 当前职位 */
  currentPosition?: string;
  /** 标签 */
  tags?: string[];
  /** 技能 */
  skills?: string[];
  /** 性别 */
  gender?: string;
  /** 期望薪资下限 */
  expectedSalaryMin?: number;
  /** 期望薪资上限 */
  expectedSalaryMax?: number;
  /** 人才姓名（用于搜索） */
  name?: string;
  /** 年龄下限 */
  ageMin?: number;
  /** 年龄上限 */
  ageMax?: number;
  /** 电话 */
  phone?: string;
  /** 邮箱 */
  email?: string;
  /** 现薪资下限 */
  currentSalaryMin?: number;
  /** 现薪资上限 */
  currentSalaryMax?: number;
  /** 工作年限下限 */
  workExperienceMin?: number;
  /** 工作年限上限 */
  workExperienceMax?: number;
  /** 来源渠道 */
  source?: string;
  /** 人才ID（UUID） */
  id?: string;
  /** 排序方式：'recentContact' 最近联系，'createdTime' 创建时间 */
  sortBy?: 'recentContact' | 'createdTime';
}

/**
 * 人才分页响应
 */
export interface TalentListResponse {
  /** 数据列表 */
  data: Talent[];
  /** 总数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 单页数量 */
  limit: number;
}

/**
 * 人才技能载荷
 */
export interface TalentSkillPayload {
  skillName: string;
  proficiency?: string;
  years?: number;
  description?: string;
}

/**
 * 人才备注
 */
export interface TalentNote {
  /** 备注 UUID */
  uuid: string;
  /** 备注 ID */
  id?: number;
  /** 关联简历 ID */
  resumeId?: number;
  /** 备注类型 */
  type?: string;
  /** 备注内容 */
  content: string;
  /** 备注状态 */
  status?: string;
  /** 提醒对象 */
  reminder?: string;
  /** 提醒时间 */
  remindTime?: number;
  /** 录音文件 URL */
  audioUrl?: string;
  /** 转写文字内容 */
  transcriptionText?: string;
  /** 录音时长（秒） */
  audioDuration?: number;
  /** 转写状态 */
  transcriptionStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  /** 转写错误信息 */
  transcriptionError?: string;
  /** 创建时间 */
  createdTime?: number;
  /** 更新时间 */
  updatedTime?: number;
  /** 创建人姓名 */
  createdByName?: string | null;
}

export interface InitialTalentRemarkPayload {
  content: string;
  reminder?: string;
  remindTime?: number;
  audioUrl?: string;
  transcriptionText?: string;
  audioDuration?: number;
}

/**
 * 人才创建/更新载荷
 */
export interface TalentCreatePayload {
  name: string;
  gender?: string;
  age?: number;
  phone?: string[];
  email?: string;
  currentCompany?: string;
  currentPosition?: string;
  location?: string;
  currentSalary?: number;
  currentSalaryRemark?: string;
  expectedSalary?: number;
  workExperience?: number;
  education?: string;
  status?: string;
  tags?: string[];
  skills?: string[];
  notes?: string;
  initialRemarks?: InitialTalentRemarkPayload[];
  wechat?: string;
  source?: string;
  languageAbility?: string;
  hukou?: string;
  birthDate?: string;
  avatarPath?: string;
  resumePath?: string;
  resumeFile?: {
    fileUrl: string;
    filePath?: string;
    fileName: string;
    fileType: string;
    fileSize?: number;
    uploadSource?: string;
    parseStatus?: string;
    parsedData?: string | Record<string, unknown>;
    parseError?: string;
    parsedAt?: string | Date;
  };
  workHistories?: Array<{
    companyName?: string;
    department?: string;
    position?: string;
    startDate?: string;
    endDate?: string;
    isCurrent?: boolean;
    description?: string;
  }>;
  projects?: Array<{
    projectName?: string;
    role?: string;
    startDate?: string;
    endDate?: string;
    isCurrent?: boolean;
    description?: string;
  }>;
  educations?: Array<{
    school?: string;
    major?: string;
    degree?: string;
    startDate?: string;
    endDate?: string;
    isFullTime?: boolean;
    is985?: boolean;
    is211?: boolean;
    isQs200?: boolean;
    description?: string;
  }>;
  talentSkills?: TalentSkillPayload[];
  certificates?: Array<{
    certificateName?: string;
    issuer?: string;
    obtainedAt?: string;
    expiredAt?: string;
    grade?: string;
    description?: string;
  }>;
  expectedJobs?: Array<{
    position?: string;
    industry?: string;
    city?: string;
    salaryMin?: number;
    salaryMax?: number;
    description?: string;
  }>;
  /** 所有者 UUID（显式传入时进入私池；不传则进入公海） */
  ownerUuid?: string;
  /** 加入当前登录顾问的私池，归属由后端确定 */
  addToPrivatePool?: boolean;
}

export interface TalentPrivatePoolQuotaResponse {
  currentCount: number;
  limit: number;
  isUnlimited: boolean;
  reached: boolean;
}

/**
 * 人才简历附件信息
 */
export interface TalentResume {
  /** 业务唯一标识 */
  uuid: string;
  /** 关联人才 UUID */
  talentUuid: string;
  /** 文件名称 */
  fileName: string;
  /** 文件类型（MIME 或简要类型） */
  fileType: string;
  /** 文件大小（字节） */
  fileSize: number;
  /** 文件访问 URL */
  fileUrl: string;
  /** 文件存储路径 */
  filePath: string;
  /** 是否主要简历 */
  isPrimary: boolean;
  /** 简历状态 */
  status: string;
  /** 简历类型 */
  resumeType: string;
  /** 上传来源 */
  uploadSource?: string;
  /** 解析状态 */
  parseStatus?: string;
  /** 解析时间 */
  parsedAt?: string;
  /** 创建时间 */
  createdAt?: string;
}

/**
 * 人才列表查询参数
 */
export interface TalentListParams {
  /** 页码 */
  page?: number;
  /** 每页数量 */
  limit?: number;
  /** 搜索关键词 */
  keywords?: string;
  /** 关键词匹配模式：'all' 全部关键词，'any' 任意关键词 */
  keywordMatchMode?: 'all' | 'any';
  /** 状态筛选 */
  status?: string;
  /** 排序方式 */
  sortBy?: 'recentContact' | 'createdTime';
  /** 其他查询参数 */
  [key: string]: any;
}

/**
 * 过滤并序列化查询参数
 *
 * @param params - 原始查询对象
 */
const buildTalentParams = (
  params?: TalentListParams,
): Record<string, string | number> | undefined => {
  if (!params) {
    return undefined;
  }

  const result: Record<string, string | number> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        result[key] = value.join(',');
      }
      return;
    }

    result[key] = value as string | number;
  });

  return Object.keys(result).length ? result : undefined;
};
export type OnlineResume = Record<string, any>;
/**
 * 人才管理 API
 */
export const talentApi = {
  /**
   * 查询人才列表
   *
   * @param params - 查询条件
   */
  getList: (params?: TalentListParams) => {
    return request<TalentListResponse>('/bff/luole-hunter/talents', {
      params: buildTalentParams(params),
    });
  },

  getListWithResumeSummary: (params?: TalentListParams) => {
    return request<TalentListResponse>(
      '/bff/luole-hunter/talents/with-resume-summary',
      {
        params: buildTalentParams(params),
      },
    );
  },

  /**
   * 查询我创建的人才
   */
  getMyTalents: (params?: TalentListParams) => {
    return request<TalentListResponse>('/bff/luole-hunter/talents/my-talents', {
      params: buildTalentParams(params),
    });
  },

  getMyTalentsWithResumeSummary: (params?: TalentListParams) => {
    return request<TalentListResponse>(
      '/bff/luole-hunter/talents/my-talents/with-resume-summary',
      {
        params: buildTalentParams(params),
      },
    );
  },

  /**
   * 查询我的私有人才
   */
  getMyPrivateTalents: (params?: TalentListParams) => {
    return request<TalentListResponse>(
      '/bff/luole-hunter/talents/my-private-talents',
      {
        params: buildTalentParams(params),
      },
    );
  },

  getMyPrivateTalentsWithResumeSummary: (params?: TalentListParams) => {
    return request<TalentListResponse>(
      '/bff/luole-hunter/talents/my-private-talents/with-resume-summary',
      {
        params: buildTalentParams(params),
      },
    );
  },

  /**
   * 查询项目添加候选人可选私池人才（组织链可管理范围）
   */
  getProjectCandidatePrivateTalents: (params?: TalentListParams) => {
    return request<TalentListResponse>(
      '/bff/luole-hunter/talents/project-candidate-private-talents',
      {
        params: buildTalentParams(params),
      },
    );
  },

  getProjectCandidatePrivateTalentsWithResumeSummary: (
    params?: TalentListParams,
  ) => {
    return request<TalentListResponse>(
      '/bff/luole-hunter/talents/project-candidate-private-talents/with-resume-summary',
      {
        params: buildTalentParams(params),
      },
    );
  },

  /**
   * 查询公海人才
   */
  getPublicPoolTalents: (params?: TalentListParams) => {
    return request<TalentListResponse>(
      '/bff/luole-hunter/talents/public-pool-talents',
      {
        params: buildTalentParams(params),
      },
    );
  },

  getPublicPoolTalentsWithResumeSummary: (params?: TalentListParams) => {
    return request<TalentListResponse>(
      '/bff/luole-hunter/talents/public-pool-talents/with-resume-summary',
      {
        params: buildTalentParams(params),
      },
    );
  },

  /**
   * 获取人才详情
   *
   * @param uuid - 人才唯一标识
   */
  getDetail: (uuid: string) => {
    return request<Talent>(`/bff/luole-hunter/talents/${uuid}`);
  },

  /**
   * 获取人才详情（项目上下文）
   *
   * @description 在项目详情页面中使用，权限规则：公海人才、自己私池、或项目创建人/负责人可查看他人私池人才
   * @param projectUuid - 项目 UUID
   * @param talentUuid - 人才 UUID
   */
  getDetailInProject: (projectUuid: string, talentUuid: string) => {
    return request<Talent>(
      `/bff/luole-hunter/projects/${projectUuid}/talents/${talentUuid}`,
    );
  },

  /**
   * 获取人才完整详情（项目上下文）
   *
   * @description 在项目详情页面中使用，权限规则：公海人才、自己私池、或项目创建人/负责人可查看他人私池人才
   * @param projectUuid - 项目 UUID
   * @param talentUuid - 人才 UUID
   */
  getDetailWithInfoInProject: (projectUuid: string, talentUuid: string) => {
    return request<{
      talent: Talent;
      projects: any[];
      communications: any[];
      resumes: TalentResume[];
    }>(
      `/bff/luole-hunter/projects/${projectUuid}/talents/${talentUuid}/details`,
    );
  },

  /**
   * 获取在线简历
   *
   * @param uuid - 人才唯一标识
   */
  getOnlineResumeByTalentUuid: (uuid: string) => {
    return request<OnlineResume>(
      `/bff/luole-hunter/talents/${uuid}/online-resume`,
    );
  },

  /**
   * 获取在线简历（项目上下文）
   *
   * @description 在项目详情页面中使用，权限规则：公海人才、自己私池、或项目创建人/负责人可查看他人私池人才
   * @param projectUuid - 项目 UUID
   * @param talentUuid - 人才 UUID
   */
  getOnlineResumeInProject: (projectUuid: string, talentUuid: string) => {
    return request<OnlineResume>(
      `/bff/luole-hunter/projects/${projectUuid}/talents/${talentUuid}/online-resume`,
    );
  },

  /**
   * 创建人才
   *
   * @param data - 人才表单数据
   */
  create: (data: Partial<Talent>) => {
    return request<Talent>('/bff/luole-hunter/talents', {
      method: 'POST',
      data,
    });
  },

  /**
   * 更新人才
   *
   * @param uuid - 人才唯一标识
   * @param data - 更新内容
   */
  update: (uuid: string, data: Partial<Talent>) => {
    return request<Talent>(`/bff/luole-hunter/talents/${uuid}`, {
      method: 'PATCH',
      data,
    });
  },

  /**
   * 删除人才（软删除）
   *
   * @param uuid - 人才唯一标识
   */
  delete: (uuid: string) => {
    return request<{ message: string }>(`/bff/luole-hunter/talents/${uuid}`, {
      method: 'DELETE',
    });
  },

  /**
   * 批量删除人才
   *
   * @param talentUuids - 人才 UUID 数组
   */
  batchDelete: (talentUuids: string[]) => {
    return request<{
      success: boolean;
      message: string;
      total: number;
      successCount: number;
      failCount: number;
      successList: string[];
      failList: Array<{ uuid: string; reason: string; name?: string }>;
    }>('/bff/luole-hunter/talents/batch-delete', {
      method: 'POST',
      data: { talentUuids },
    });
  },

  /**
   * 获取指定人才的简历附件列表
   */
  getTalentResumes: (talentUuid: string) => {
    return request<TalentResume[]>(
      `/bff/luole-hunter/talents/${talentUuid}/resumes`,
    );
  },

  /**
   * 删除简历附件（软删除）
   */
  removeTalentResume: (resumeUuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/talents/resumes/${resumeUuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 设置指定简历为主要简历
   */
  setPrimaryTalentResume: (resumeUuid: string) => {
    return request<TalentResume>(
      `/bff/luole-hunter/talents/resumes/${resumeUuid}/set-primary`,
      {
        method: 'POST',
      },
    );
  },

  /**
   * 单个加入私池
   *
   * @param uuid - 人才 UUID
   */
  addToPrivatePool: (uuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/talents/${uuid}/add-to-private-pool`,
      {
        method: 'POST',
        skipErrorHandler: true,
      },
    );
  },

  /**
   * 单个移除私池
   *
   * @param uuid - 人才 UUID
   */
  removeFromPrivatePool: (uuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/talents/${uuid}/remove-from-private-pool`,
      {
        method: 'POST',
        skipErrorHandler: true,
      },
    );
  },

  /**
   * 批量加入私池
   *
   * @param talentUuids - 人才 UUID 数组
   */
  batchAddToPrivatePool: (talentUuids: string[]) => {
    return request<{
      success: boolean;
      message: string;
      total: number;
      successCount: number;
      failCount: number;
      successList: string[];
      failList: Array<{ uuid: string; reason: string; name?: string }>;
    }>('/bff/luole-hunter/talents/batch-add-to-private-pool', {
      method: 'POST',
      data: { talentUuids },
      skipErrorHandler: true,
    });
  },

  /**
   * 批量移除私池
   *
   * @param talentUuids - 人才 UUID 数组
   */
  batchRemoveFromPrivatePool: (talentUuids: string[]) => {
    return request<{
      success: boolean;
      message: string;
      total: number;
      successCount: number;
      failCount: number;
      successList: string[];
      failList: Array<{ uuid: string; reason: string; name?: string }>;
    }>('/bff/luole-hunter/talents/batch-remove-from-private-pool', {
      method: 'POST',
      data: { talentUuids },
      skipErrorHandler: true,
    });
  },

  /**
   * 获取当前登录人人才私池上限及占用情况
   */
  getPrivatePoolQuota: () => {
    return request<TalentPrivatePoolQuotaResponse>(
      '/bff/luole-hunter/talents/private-pool-quota',
    );
  },

  /**
   * 获取人才的工作经历列表
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentWorkHistories: (talentUuid: string) => {
    return request<any[]>(
      `/bff/luole-hunter/talents/${talentUuid}/work-history`,
    );
  },

  /**
   * 获取人才的教育经历列表
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentEducations: (talentUuid: string) => {
    return request<any[]>(`/bff/luole-hunter/talents/${talentUuid}/education`);
  },

  /**
   * 获取人才的项目经历列表
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentProjects: (talentUuid: string) => {
    return request<any[]>(`/bff/luole-hunter/talents/${talentUuid}/projects`);
  },

  /**
   * 获取人才的期望职位列表
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentExpectedJobs: (talentUuid: string) => {
    return request<any[]>(
      `/bff/luole-hunter/talents/${talentUuid}/expected-jobs`,
    );
  },

  /**
   * 获取人才的技能列表
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentSkills: (talentUuid: string) => {
    return request<any[]>(`/bff/luole-hunter/talents/${talentUuid}/skills`);
  },

  /**
   * 获取人才的证书列表
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentCertificates: (talentUuid: string) => {
    return request<any[]>(
      `/bff/luole-hunter/talents/${talentUuid}/certificates`,
    );
  },

  /**
   * 获取人才参与的项目列表（通过候选人关联查询）
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentParticipatedProjects: (talentUuid: string) => {
    return request<any[]>(
      `/bff/luole-hunter/talents/${talentUuid}/participated-projects`,
    );
  },

  /**
   * 获取人才的沟通记录列表
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentCommunications: (talentUuid: string) => {
    return request<any[]>(
      `/bff/luole-hunter/talents/${talentUuid}/communications`,
    );
  },

  /**
   * 获取人才完整详情（包含项目、沟通记录等）
   *
   * @param uuid - 人才 UUID
   */
  getDetailWithInfo: (uuid: string) => {
    return request<{
      talent: Talent;
      projects: any[];
      communications: any[];
      resumes: TalentResume[];
    }>(`/bff/luole-hunter/talents/${uuid}/details`);
  },

  /**
   * 获取各个分类的人才数量，用于在菜单上显示气泡数字
   *
   * @returns 各分类的人才数量
   */
  getTalentCounts: async () => {
    try {
      const response = await request<{
        all: number;
        my: number;
        private: number;
        public: number;
      }>('/bff/luole-hunter/talents/counts');

      return {
        all: response.all || 0,
        my: response.my || 0,
        private: response.private || 0,
        public: response.public || 0,
      };
    } catch (error) {
      console.error('Failed to get talent counts:', error);
      return {
        all: 0,
        my: 0,
        private: 0,
        public: 0,
      };
    }
  },

  /**
   * 检查手机号是否存在
   *
   * @param phone - 手机号
   * @param excludeUuid - 排除的人才 UUID（编辑时使用）
   */
  checkPhoneExists: (phone: string, excludeUuid?: string) => {
    const params = new URLSearchParams({ phone });
    if (excludeUuid) params.append('excludeUuid', excludeUuid);
    return request<{ exists: boolean; message?: string }>(
      `/bff/luole-hunter/talents/check-phone?${params.toString()}`,
      { method: 'GET' },
    );
  },

  /**
   * 上传头像
   *
   * @param file - 头像文件
   */
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return request<{ avatarPath: string }>(
      '/bff/luole-hunter/talents/upload-avatar',
      {
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  },

  /**
   * 仅上传简历文件（不创建人才）
   *
   * @param file - 简历文件
   * @param onProgress - 上传进度回调
   */
  uploadResumeFileOnly: (
    file: File,
    onProgress?: (percent: number) => void,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    return request<{
      fileUrl: string;
      filePath?: string;
      fileName: string;
      fileType: string;
      fileSize?: number;
    }>('/bff/luole-hunter/talents/upload-resume-file-only', {
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: any) => {
        if (progressEvent.total && onProgress) {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(percent);
        }
      },
    });
  },

  /**
   * 解析简历
   *
   * @param params - 解析参数
   */
  parseResume: (params: { fileUrl: string; filePath?: string }) => {
    return request<{
      success: boolean;
      data?: {
        result?: any;
        data?: any;
      };
      message?: string;
    }>('/bff/luole-hunter/talents/parse-resume', {
      method: 'POST',
      data: params,
    });
  },

  /**
   * 获取人才测试列表
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentTests: (talentUuid: string) => {
    return request<any[]>(`/bff/luole-hunter/talents/${talentUuid}/tests`);
  },

  /**
   * 创建人才测试
   *
   * @param talentUuid - 人才 UUID
   * @param data - 测试数据
   */
  createTalentTest: (talentUuid: string, data: any) => {
    return request<any>(`/bff/luole-hunter/talents/${talentUuid}/tests`, {
      method: 'POST',
      data,
    });
  },

  /**
   * 更新人才测试
   *
   * @param talentUuid - 人才 UUID
   * @param testUuid - 测试 UUID
   * @param data - 测试数据
   */
  updateTalentTest: (talentUuid: string, testUuid: string, data: any) => {
    return request<any>(
      `/bff/luole-hunter/talents/${talentUuid}/tests/${testUuid}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  /**
   * 删除人才测试
   *
   * @param talentUuid - 人才 UUID
   * @param testUuid - 测试 UUID
   */
  deleteTalentTest: (talentUuid: string, testUuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/talents/${talentUuid}/tests/${testUuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 从简历 URL 创建人才
   *
   * @param resumeUrl - 简历文件 URL
   * @param phone - 手机号（可选）
   * @param email - 邮箱（可选）
   * @param age - 年龄（可选）
   * @param fileInfo - 文件信息
   * @param fileInfo.fileName - 文件名
   * @param fileInfo.fileSize - 文件大小
   * @param fileInfo.fileType - 文件类型
   */
  createFromResumeUrl: (
    resumeUrl: string,
    phone?: string,
    email?: string,
    age?: number,
    fileInfo?: {
      fileName?: string;
      fileSize?: number;
      fileType?: string;
    },
  ) => {
    return request<Talent>('/bff/luole-hunter/talents/create-from-resume-url', {
      method: 'POST',
      data: {
        resumeUrl,
        phone,
        email,
        age,
        ...fileInfo,
      },
    });
  },

  /**
   * 获取人才的备注列表
   *
   * @param talentUuid - 人才 UUID
   */
  getNotes: (talentUuid: string) => {
    return request<any[]>(
      `/bff/luole-hunter/resume-remarks/talents/${talentUuid}`,
    );
  },

  /**
   * 创建人才备注
   *
   * @param talentUuid - 人才 UUID
   * @param data - 备注数据（可含录音链接与转写，与客户沟通记录逻辑一致）
   */
  createNote: (
    talentUuid: string,
    data: {
      content: string;
      reminder?: string;
      remindTime?: number;
      audioUrl?: string;
      transcriptionText?: string;
      audioDuration?: number;
    },
  ) => {
    return request<any>(
      `/bff/luole-hunter/resume-remarks/talents/${talentUuid}`,
      {
        method: 'POST',
        data,
      },
    );
  },

  /**
   * 上传备注录音并分析：云存储 + 语音转写 + AI 摘要与要点（与客户沟通记录逻辑一致）
   */
  uploadNoteAudioAndAnalyze: (
    file: Blob,
    audioDurationSeconds?: number,
  ): Promise<{
    audioUrl: string;
    content: string;
    transcriptionText: string;
    keyPoints: string[];
    audioDuration?: number;
  }> => {
    const formData = new FormData();
    formData.append('file', file, `recording_${Date.now()}.webm`);
    if (audioDurationSeconds != null && !Number.isNaN(audioDurationSeconds)) {
      formData.append(
        'audioDuration',
        String(Math.round(audioDurationSeconds)),
      );
    }
    return request(
      '/bff/luole-hunter/resume-remarks/actions/upload-audio-and-analyze',
      {
        method: 'POST',
        data: formData,
      },
    );
  },

  /**
   * 更新人才备注
   *
   * @param remarkId - 备注 ID
   * @param data - 备注数据
   */
  updateNote: (
    remarkId: number,
    data: {
      content?: string;
      reminder?: string;
      remindTime?: number;
      audioUrl?: string;
      transcriptionText?: string;
      audioDuration?: number;
    },
  ) => {
    return request<any>(`/bff/luole-hunter/resume-remarks/${remarkId}`, {
      method: 'PUT',
      data,
    });
  },

  /**
   * 删除人才备注
   *
   * @param remarkId - 备注 ID
   */
  deleteNote: (remarkId: number) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/resume-remarks/${remarkId}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 上传备注录音文件
   *
   * @param uuid - 备注 UUID
   * @param file - 录音文件（Blob 类型）
   * @param durationSeconds - 录音时长（秒）
   */
  uploadNoteAudio: (
    uuid: string,
    file: Blob,
    durationSeconds?: number,
  ): Promise<TalentNote> => {
    const formData = new FormData();
    formData.append('file', file, `recording_${Date.now()}.webm`);
    if (durationSeconds != null && !Number.isNaN(durationSeconds)) {
      formData.append('duration', String(Math.round(durationSeconds)));
    }
    return request<TalentNote>(
      `/bff/luole-hunter/talents/notes/${uuid}/upload-audio`,
      {
        method: 'POST',
        data: formData,
      },
    );
  },

  /**
   * 删除工作经历
   *
   * @param uuid - 工作经历 UUID
   */
  removeWorkHistory: (uuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/talents/work-history/${uuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 删除教育经历
   *
   * @param uuid - 教育经历 UUID
   */
  removeEducation: (uuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/talents/education/${uuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 删除项目经历
   *
   * @param uuid - 项目经历 UUID
   */
  removeProject: (uuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/talents/projects/${uuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 删除沟通记录
   *
   * @param uuid - 沟通记录 UUID
   */
  removeCommunication: (uuid: string) => {
    return request<{ message: string }>(
      `/bff/luole-hunter/talents/communications/${uuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 创建人才点击记录
   *
   * @param talentUuid - 人才 UUID
   */
  createTalentClickRecord: (talentUuid: string) => {
    return request<any>(
      `/bff/luole-hunter/talents/${talentUuid}/click-records`,
      {
        method: 'POST',
      },
    );
  },

  /**
   * 获取人才的点击记录列表
   *
   * @param talentUuid - 人才 UUID
   */
  getTalentClickRecords: (talentUuid: string) => {
    return request<any[]>(
      `/bff/luole-hunter/talents/${talentUuid}/click-records`,
    );
  },

  /**
   * 为指定人才上传简历附件
   *
   * @param params - 上传参数
   */
  uploadTalentResume: (params: {
    talentUuid: string;
    file: File;
    resumeType?: string;
    isPrimary?: boolean;
    description?: string;
  }) => {
    const formData = new FormData();
    formData.append('file', params.file, params.file.name);
    formData.append('talentUuid', params.talentUuid);
    if (params.resumeType) {
      formData.append('resumeType', params.resumeType);
    }
    if (typeof params.isPrimary === 'boolean') {
      formData.append('isPrimary', String(params.isPrimary));
    }
    if (params.description) {
      formData.append('description', params.description);
    }

    return request<any>('/bff/luole-hunter/talents/upload-resume', {
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * 获取简历原件的 Base64 数据
   *
   * @description 后端通过简历路径将文件转换为 Base64 编码的 data URI
   * @param resumePath - 简历文件路径（可以是相对路径或完整 URL）
   * @returns Base64 编码的 data URI（格式：data:application/pdf;base64,...）或包含 base64 字段的对象
   */
  getResumePreviewBase64: (resumePath: string) => {
    return request<string | { base64: string }>(
      '/bff/luole-hunter/talents/resume-preview',
      {
        params: { resumePath },
      },
    );
  },

  /**
   * 合并人才
   *
   * @description 将多个源人才的数据合并到目标人才，然后删除源人才
   * @param targetTalentUuid - 目标人才UUID（保留）
   * @param sourceTalentUuids - 源人才UUID列表（合并后删除）
   */
  mergeTalents: (
    targetTalentUuid: string,
    sourceTalentUuids: string[],
    fieldOverrides?: Record<string, any>,
  ) => {
    return request<{
      success: boolean;
      message: string;
      mergedData: {
        resumes: number;
        workHistories: number;
        educations: number;
        projects: number;
        expectedJobs: number;
        skills: number;
        certificates: number;
        projectCandidates: number;
        interviews: number;
        recommendationReports: number;
        collectionTalents: number;
      };
      deletedSourceTalents: string[];
    }>('/bff/luole-hunter/talents/merge', {
      method: 'POST',
      data: { targetTalentUuid, sourceTalentUuids, fieldOverrides },
    });
  },
};
